import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Snackbar, TextField, Typography,
} from '@mui/material';
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined';
import { fetchGabbaiMembers } from '../gabbaiPortalApi';
import { fieldSxFromTheme, getGabbaiTheme } from '../gabbaiPortalTheme';
import MemberRegisterDialog from './MemberRegisterDialog';

export default function MembersScreen({ activeKolelId, meta }) {
  const t = getGabbaiTheme();
  const fieldSx = fieldSxFromTheme(t);
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [toast, setToast] = useState('');

  useEffect(() => {
    const id = setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => clearTimeout(id);
  }, [q]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (activeKolelId) params.kolelId = activeKolelId;
      if (debouncedQ) params.q = debouncedQ;
      const data = await fetchGabbaiMembers(params);
      setMembers(data.data || []);
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה בטעינת החברים');
    } finally {
      setLoading(false);
    }
  }, [activeKolelId, debouncedQ]);

  useEffect(() => { load(); }, [load]);

  return (
    <Box sx={{ maxWidth: 640, mx: 'auto' }}>
      <TextField
        fullWidth
        label="חיפוש לפי שם או טלפון"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        sx={{ mb: 2, ...fieldSx }}
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading && (
        <Box sx={{ display: 'grid', placeItems: 'center', py: 6 }}>
          <CircularProgress sx={{ color: t.accent }} />
        </Box>
      )}

      {!loading && !members.length && (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          אין חברים משויכים לכולל זה
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {members.map((m) => (
          <Box
            key={m.id}
            sx={{
              border: `1px solid ${t.cardBorder}`,
              background: t.cardBg,
              borderRadius: 2.5,
              px: 2,
              py: 1.75,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'flex-start' }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ color: '#f5f0e6', fontWeight: 700, fontSize: '1.05rem' }}>
                  {m.firstName} {m.lastName}
                </Typography>
                <Typography sx={{ color: 'rgba(245,240,230,0.65)', fontSize: '0.9rem', dir: 'ltr' }}>
                  {m.phone || '—'}
                  {m.city ? ` · ${m.city}` : ''}
                </Typography>
                {!!m.kolels?.length && (
                  <Typography sx={{ color: 'rgba(245,240,230,0.5)', fontSize: '0.8rem', mt: 0.25 }}>
                    {m.kolels.join(' · ')}
                  </Typography>
                )}
                <Typography sx={{ color: t.accentSoft, fontSize: '0.85rem', mt: 0.75 }}>
                  {m.registrationCount
                    ? `${m.registrationCount} הרשמות לתקופה הפעילה`
                    : 'אין הרשמות לתקופה הפעילה'}
                </Typography>
                {!!m.registrations?.length && (
                  <Typography sx={{ color: 'rgba(245,240,230,0.55)', fontSize: '0.78rem', mt: 0.25 }}>
                    {(m.registrations || [])
                      .slice(0, 4)
                      .map((r) => r.exam?.code || r.exam?.name || '')
                      .filter(Boolean)
                      .join(' · ')}
                    {m.registrations.length > 4 ? '…' : ''}
                  </Typography>
                )}
              </Box>
              <Button
                size="small"
                startIcon={<HowToRegOutlinedIcon />}
                onClick={() => setSelected(m)}
                sx={{
                  flexShrink: 0,
                  fontWeight: 700,
                  color: t.ctaColor,
                  background: t.ctaBg,
                  borderRadius: 2,
                  px: 1.5,
                  '&:hover': { filter: 'brightness(1.06)' },
                }}
              >
                רישום
              </Button>
            </Box>
          </Box>
        ))}
      </Box>

      <MemberRegisterDialog
        open={Boolean(selected)}
        member={selected}
        meta={meta}
        onClose={() => setSelected(null)}
        onSaved={() => {
          setToast('הרישום נשמר בהצלחה');
          load();
        }}
      />

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={2500}
        onClose={() => setToast('')}
        message={toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}
